import { useCallback, useEffect, useState } from 'react'

import { useAuth } from '@/hooks/useAuth'
import { useBiometric } from '@/hooks/useBiometric'
import { useDisclosure } from '@/hooks/useDisclosure'

import { biometricUtils } from '@/utils/biometric'

interface UseBiometricLoginParams {
  onSuccess: () => void
}

export const useBiometricLogin = ({ onSuccess }: UseBiometricLoginParams) => {
  const biometric = useBiometric()
  const { hasStoredRefresh } = useAuth()

  const enablePrompt = useDisclosure()
  const [showLoginButton, setShowLoginButton] = useState(false)

  useEffect(() => {
    if (biometric.isChecking) return

    if (!biometric.available) {
      setShowLoginButton(false)
      return
    }

    const controller = new AbortController()
    const { signal } = controller

    const run = async () => {
      const enabled = await biometricUtils.isEnabled()
      const hasRefresh = await hasStoredRefresh()
      if (signal.aborted) return
      setShowLoginButton(enabled && hasRefresh)
    }

    run()

    return () => {
      controller.abort()
    }
  }, [biometric.available, biometric.isChecking])

  const triggerBiometricPrompt = useCallback(async () => {
    const preferenceSet = await biometricUtils.hasPreferenceSet()
    if (biometric.available && !preferenceSet) {
      enablePrompt.open()
      return
    }
    onSuccess()
  }, [biometric.available, enablePrompt, onSuccess])

  const handleClosePrompt = useCallback(() => {
    enablePrompt.close()
    onSuccess()
  }, [enablePrompt, onSuccess])

  return {
    isChecking: biometric.isChecking,
    showLoginButton,
    enablePrompt,
    triggerBiometricPrompt,
    handleClosePrompt,
  }
}
